/**
 * P&L Multi-Period Comparison — /finance/reports/pl-compare
 * G09: Compare profit & loss across multiple periods side-by-side.
 * Calls GET /finance/reports/profit-loss-compare?periods[]=2024-01-01:2024-03-31&periods[]=...
 */

import { useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, RefreshCw, Plus, Trash2 } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PeriodCompareRow {
  section: 'revenue' | 'cost_of_sales' | 'gross_profit' | 'operating_expense' | 'net_profit';
  label: string;
  values: number[];
  isSummary?: boolean;
}

interface PlCompareData {
  periods: Array<{ from: string; to: string; label: string }>;
  rows: PeriodCompareRow[];
}

interface PeriodRange {
  from: string;
  to: string;
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function pctChange(base: number, compare: number): string {
  if (base === 0) return compare === 0 ? '—' : '+∞';
  const pct = ((compare - base) / Math.abs(base)) * 100;
  return (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%';
}

function currentMonth(): PeriodRange {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const last = new Date(y, now.getMonth() + 1, 0).getDate();
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${last}` };
}

function lastMonth(): PeriodRange {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const last = new Date(y, d.getMonth() + 1, 0).getDate();
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${last}` };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PlComparePage() {
  const lm = lastMonth();
  const cm = currentMonth();
  const [periods, setPeriods] = useState<PeriodRange[]>([
    { from: lm.from, to: lm.to },
    { from: cm.from, to: cm.to },
  ]);
  const [data, setData]     = useState<PlCompareData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const fetchData = useCallback(async (ps: PeriodRange[]) => {
    if (ps.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const qs = ps.map(p => `periods[]=${p.from}:${p.to}`).join('&');
      const res = await api.get<PlCompareData>(`/finance/reports/profit-loss-compare?${qs}`);
      setData(res ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const addPeriod = () => {
    const last = periods[periods.length - 1];
    if (!last) return;
    // go back one more month from the earliest from date
    const d = new Date(last.from + 'T00:00:00');
    d.setMonth(d.getMonth() - 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const endDay = new Date(y, d.getMonth() + 1, 0).getDate();
    setPeriods(prev => [...prev, { from: `${y}-${m}-01`, to: `${y}-${m}-${endDay}` }]);
  };

  const removePeriod = (idx: number) => {
    setPeriods(prev => prev.filter((_, i) => i !== idx));
  };

  const updatePeriod = (idx: number, field: 'from' | 'to', value: string) => {
    setPeriods(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const SECTION_COLOR: Record<string, string> = {
    net_profit:     'bg-green-50 font-bold text-green-800',
    gross_profit:   'bg-blue-50 font-semibold text-blue-800',
    revenue:        '',
    cost_of_sales:  '',
    operating_expense: '',
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-6 w-6 text-green-600" />
          <div>
            <h1 className="text-xl font-semibold">P&L Comparison</h1>
            <p className="text-sm text-muted-foreground">Compare profit & loss across multiple periods</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addPeriod} disabled={periods.length >= 6}>
            <Plus className="h-4 w-4 mr-1" /> Add Period
          </Button>
          <Button size="sm" onClick={() => fetchData(periods)} disabled={loading || periods.length === 0}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Compare
          </Button>
        </div>
      </div>

      {/* Period selectors */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            {periods.map((p, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-muted/40 rounded-lg px-3 py-2">
                <span className="text-xs font-medium text-muted-foreground w-16">Period {idx + 1}</span>
                <input
                  type="date" value={p.from}
                  onChange={e => updatePeriod(idx, 'from', e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
                <span className="text-muted-foreground text-xs">→</span>
                <input
                  type="date" value={p.to}
                  onChange={e => updatePeriod(idx, 'to', e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
                {periods.length > 2 && (
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => removePeriod(idx)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Results table */}
      {data && (
        <Card>
          <CardContent className="pt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="font-semibold whitespace-nowrap">Line Item</TableHead>
                  {data.periods.map((p, i) => (
                    <TableHead key={i} className="text-right font-semibold whitespace-nowrap">
                      {p.label || `${p.from} ~ ${p.to}`}
                    </TableHead>
                  ))}
                  {data.periods.length >= 2 && (
                    <TableHead className="text-right font-semibold text-muted-foreground whitespace-nowrap">vs P1</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row, ri) => (
                  <TableRow key={ri} className={`${SECTION_COLOR[row.section] ?? ''} hover:bg-muted/10`}>
                    <TableCell className={`${row.isSummary ? '' : 'pl-8 text-muted-foreground'}`}>
                      {row.label}
                    </TableCell>
                    {row.values.map((v, vi) => (
                      <TableCell key={vi} className={`text-right font-mono whitespace-nowrap ${v < 0 ? 'text-red-600' : ''}`}>
                        {fmt(v)}
                      </TableCell>
                    ))}
                    {data.periods.length >= 2 && (
                      <TableCell className="text-right text-xs text-muted-foreground font-mono whitespace-nowrap">
                        {row.values.length >= 2
                          ? pctChange(row.values[0]!, row.values[row.values.length - 1]!)
                          : '—'}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!data && !loading && !error && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">Set your periods and click Compare</p>
            <p className="text-sm mt-1">You can compare up to 6 periods side by side</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
